import { useCallback } from 'react';

/**
 * EditorJS toolbar commands of the article editor (execCommand formatting,
 * list conversion, block insertion and paragraph/heading style switch).
 */
// editorRef (ref object) and setSelectedBlockType (state setter) are stable identities.
export function useArticleEditorCommands({ editorRef, handleEditorChange, setSelectedBlockType }) {
  const runEditorCommand = useCallback((command, value = null) => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      const firstEditable = document.querySelector('#editorjs [contenteditable="true"]');
      if (firstEditable) {
        firstEditable.focus();
      }
    }
    document.execCommand(command, false, value);
  }, []);

  const convertCurrentBlock = useCallback(async (style) => {
    const editor = editorRef.current;
    if (!editor?.blocks) return;
    try {
      const currentIndex = editor.blocks.getCurrentBlockIndex();
      if (currentIndex < 0) return;
      await editor.blocks.delete(currentIndex);
      editor.blocks.insert('list', { style, items: [''] }, {}, currentIndex, true);
      await editor.save();
      handleEditorChange();
    } catch (error) {
      console.error('Failed to convert current block:', error);
    }
  }, [editorRef, handleEditorChange]);

  const insertEditorBlock = useCallback(async (type, data = {}) => {
    const editor = editorRef.current;
    if (!editor?.blocks) return;
    try {
      const currentIndex = editor.blocks.getCurrentBlockIndex();
      const insertIndex = currentIndex >= 0 ? currentIndex + 1 : undefined;
      editor.blocks.insert(type, data, {}, insertIndex, true);
      await editor.save();
      handleEditorChange();
    } catch (error) {
      console.error('Failed to insert block:', error);
    }
  }, [editorRef, handleEditorChange]);

  const setCurrentBlockStyle = useCallback(async (nextStyle) => {
    setSelectedBlockType(nextStyle);
    const editor = editorRef.current;
    if (!editor?.blocks) return;
    try {
      const currentIndex = editor.blocks.getCurrentBlockIndex();
      if (currentIndex < 0) {
        if (nextStyle === 'paragraph') {
          await insertEditorBlock('paragraph', { text: '' });
        } else if (nextStyle === 'header-2') {
          await insertEditorBlock('header', { text: '', level: 2 });
        } else if (nextStyle === 'header-3') {
          await insertEditorBlock('header', { text: '', level: 3 });
        }
        return;
      }

      const currentBlock = editor.blocks.getBlockByIndex(currentIndex);
      const currentText = currentBlock?.holder?.innerText || '';
      await editor.blocks.delete(currentIndex);

      if (nextStyle === 'paragraph') {
        editor.blocks.insert('paragraph', { text: currentText }, {}, currentIndex, true);
      } else if (nextStyle === 'header-2') {
        editor.blocks.insert('header', { text: currentText, level: 2 }, {}, currentIndex, true);
      } else if (nextStyle === 'header-3') {
        editor.blocks.insert('header', { text: currentText, level: 3 }, {}, currentIndex, true);
      }

      await editor.save();
      handleEditorChange();
    } catch (error) {
      console.error('Failed to change block style:', error);
    }
  }, [editorRef, handleEditorChange, insertEditorBlock, setSelectedBlockType]);

  return {
    runEditorCommand,
    convertCurrentBlock,
    insertEditorBlock,
    setCurrentBlockStyle,
  };
}
